import React, { useState } from 'react';
import { X, Flag } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FlagModal = ({ isOpen, onClose, contentType, contentId, contentPreview }) => {
  const [reason, setReason] = useState('spam');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const { toast } = useToast();

  const flagReasons = [
    { value: 'spam', label: 'Spam or misleading' },
    { value: 'harassment', label: 'Harassment or hate speech' },
    { value: 'inappropriate', label: 'Inappropriate content' },
    { value: 'misinformation', label: 'Misinformation' },
    { value: 'other', label: 'Other' },
  ];

  const handleClose = () => {
    setSuccess(false);
    setError(null);
    setReason('spam');
    setComment('');
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const endpoint = contentType === 'post' 
        ? `${API}/posts/${contentId}/flag`
        : `${API}/comments/${contentId}/flag`;

      await axios.post(endpoint, {
        reason,
        comment: comment.trim() || null,
      });

      // Show success state
      setSuccess(true);
      
      toast({
        title: 'Report submitted successfully',
        description: 'Thank you for helping keep our community safe. Our moderators will review this report.',
      });

      // Close after 2 seconds to show success state
      setTimeout(() => {
        handleClose();
      }, 2000);
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to flag content';
      
      // Check if already flagged
      if (errorMessage.includes('already flagged')) {
        setError('You have already reported this content. Our team will review it.');
      } else {
        setError(errorMessage);
      }
      
      toast({
        title: 'Cannot report',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        // Close modal when clicking backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-lg max-w-md w-full p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="flex items-center space-x-2 mb-4">
          <Flag className="w-5 h-5 text-red-600" />
          <h2 className="text-xl font-bold text-gray-900">Report Content</h2>
        </div>

        {/* Content preview */}
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <p className="text-sm text-gray-600 line-clamp-2">
            "{contentPreview}"
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Reason dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Why are you reporting this?
            </label>
            <select
              value={reason}
              onChange={(e) => {
                e.stopPropagation();
                setReason(e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              required
            >
              {flagReasons.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          {/* Optional comment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional details (optional)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Provide more context..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              rows="3"
            />
          </div>

          {/* Actions */}
          <div className="flex space-x-3">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition disabled:opacity-50 font-medium"
            >
              {loading ? 'Reporting...' : 'Report'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition font-medium"
            >
              Cancel
            </button>
          </div>
        </form>

        {/* Info */}
        <p className="text-xs text-gray-500 mt-4">
          False reports may result in action against your account.
        </p>
      </div>
    </div>
  );
};

export default FlagModal;
